import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAgentRun } from './agent/useAgentRun'
import { compileDocument } from './core/serializer'
import { activeBranch, useStudioStore } from './core/store'
import { AgentPanel } from './components/AgentPanel'
import { CanvasSurface, type PreviewViewport } from './components/CanvasSurface'
import {
  ActivityRail,
  RightPanelHeader,
  StatusBar,
  TopBar,
  WorkspaceToolbar,
  type SidePanelTab,
  type WorkspaceMode,
} from './components/Chrome'
import { CodeSurface } from './components/CodeSurface'
import { ExplorerPanel } from './components/ExplorerPanel'
import { InspectorPanel } from './components/InspectorPanel'
import { PerformancePanel } from './components/PerformancePanel'
import { type PreviewMetric } from './components/SandboxPreview'
import { VersionsPanel } from './components/VersionsPanel'
import './App.css'

function App() {
  const studio = useStudioStore()
  const [mode, setMode] = useState<WorkspaceMode>('split')
  const [sidePanel, setSidePanel] = useState<SidePanelTab>('inspect')
  const [agentOpen, setAgentOpen] = useState(true)
  const [viewport, setViewport] = useState<PreviewViewport>('desktop')
  const [runtimeError, setRuntimeError] = useState<string | null>(null)
  const [previewMetric, setPreviewMetric] = useState<PreviewMetric>({
    renderMs: 0,
    nodeCount: Object.keys(studio.document.nodes).length,
    revision: 0,
  })

  const agent = useAgentRun(studio.applyPatch, studio.createCheckpoint)
  const canonicalSource = useMemo(
    () => compileDocument(studio.document),
    [studio.document],
  )
  const branch = activeBranch(studio)
  const selectedNode =
    studio.document.nodes[studio.selectedNodeId] ??
    studio.document.nodes[studio.document.rootId]

  const selectNode = useCallback(
    (nodeId: string) => {
      studio.selectNode(nodeId)
      setSidePanel('inspect')
    },
    [studio],
  )

  const toggleAgent = useCallback(() => {
    setAgentOpen((open) => {
      const next = !open
      if (next) {
        window.setTimeout(
          () =>
            document
              .querySelector<HTMLTextAreaElement>('#agent-prompt')
              ?.focus(),
          0,
        )
      }
      return next
    })
  }, [])

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const command = event.metaKey || event.ctrlKey
      if (!command) return
      const key = event.key.toLowerCase()

      if (key === 'z') {
        event.preventDefault()
        if (event.shiftKey) studio.redo()
        else studio.undo()
      }
      if (key === 's') {
        event.preventDefault()
        if (studio.editorDraft !== canonicalSource) studio.applyEditorDraft()
      }
      if (key === 'k') {
        event.preventDefault()
        toggleAgent()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [canonicalSource, studio, toggleAgent])

  return (
    <div className="studio-shell">
      <TopBar
        branchName={branch.name}
        canUndo={studio.history.length > 0}
        canRedo={studio.future.length > 0}
        onUndo={studio.undo}
        onRedo={studio.redo}
        onReset={studio.reset}
        onRemix={() => {
          studio.createRemix()
          setSidePanel('versions')
        }}
        onBranchClick={() => setSidePanel('versions')}
        onPreview={() => {
          setMode('design')
          setAgentOpen(false)
        }}
      />

      <div className="studio-workspace">
        <ActivityRail
          activeTab={sidePanel}
          agentOpen={agentOpen}
          onTabChange={setSidePanel}
          onAgentToggle={toggleAgent}
        />
        <ExplorerPanel
          document={studio.document}
          selectedNodeId={studio.selectedNodeId}
          onSelect={selectNode}
        />

        <main className="workspace-main">
          <WorkspaceToolbar
            mode={mode}
            agentOpen={agentOpen}
            nodeCount={Object.keys(studio.document.nodes).length}
            onModeChange={setMode}
            onAgentToggle={toggleAgent}
          />
          <div className={`workspace-stack ${agentOpen ? 'has-agent' : ''}`}>
            <div className={`surface-grid mode-${mode}`}>
              {mode !== 'code' ? (
                <CanvasSurface
                  document={studio.document}
                  selectedNodeId={studio.selectedNodeId}
                  viewport={viewport}
                  onViewportChange={setViewport}
                  onSelect={selectNode}
                  onMetric={setPreviewMetric}
                  onRuntimeError={setRuntimeError}
                  runtimeError={runtimeError}
                />
              ) : null}
              {mode !== 'design' ? (
                <CodeSurface
                  source={studio.editorDraft}
                  canonicalSource={canonicalSource}
                  error={studio.codeError}
                  onChange={studio.setEditorDraft}
                  onApply={studio.applyEditorDraft}
                  onFormat={studio.formatEditorDraft}
                />
              ) : null}
            </div>
            {agentOpen ? (
              <AgentPanel agent={agent} onClose={() => setAgentOpen(false)} />
            ) : null}
          </div>
        </main>

        <aside className="right-panel">
          <RightPanelHeader
            activeTab={sidePanel}
            onTabChange={setSidePanel}
          />
          {sidePanel === 'inspect' ? (
            <InspectorPanel
              node={selectedNode}
              tokens={studio.document.tokens}
              onUpdateNode={(patch, label) =>
                studio.updateNode(selectedNode.id, patch, label)
              }
              onUpdateToken={studio.updateToken}
            />
          ) : null}
          {sidePanel === 'versions' ? (
            <VersionsPanel
              branches={studio.branches}
              commits={studio.commits}
              activeBranchId={studio.activeBranchId}
              currentCode={canonicalSource}
              onRemix={studio.createRemix}
            />
          ) : null}
          {sidePanel === 'performance' ? (
            <PerformancePanel
              previewMetric={previewMetric}
              mutations={studio.mutations}
              firstTokenMs={agent.state.firstTokenMs}
              historyDepth={studio.history.length}
            />
          ) : null}
        </aside>
      </div>

      <StatusBar
        renderMs={previewMetric.renderMs}
        nodeCount={previewMetric.nodeCount}
        codeError={studio.codeError}
        agentPhase={agent.state.phase}
      />
    </div>
  )
}

export default App
